"use client";

import { create } from "@/lib/create-safe-store";

export interface AudioTrack {
  id: string;
  src: string;
  title: string;
  duration?: number | null;
  waveform?: number[] | null;
  isOwn?: boolean;
}

interface AudioState {
  activeTrack: AudioTrack | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number | null;
  buffered: number;
  playbackRate: number;
  loadTrack: (track: AudioTrack, autoplay?: boolean) => void;
  toggleTrack: (track: AudioTrack) => void;
  togglePlayback: () => void;
  seek: (time: number) => void;
  cycleSpeed: () => void;
  stop: () => void;
  _setPlaybackState: (patch: Partial<Pick<AudioState, "isPlaying" | "currentTime" | "duration" | "buffered">>) => void;
}

let audioElement: HTMLAudioElement | null = null;
let listenersBound = false;
let pollInterval: ReturnType<typeof setInterval> | null = null;

function startPolling() {
  stopPolling();
  pollInterval = setInterval(() => {
    if (!audioElement) return;
    const patch: Record<string, number> = { currentTime: audioElement.currentTime };
    if (audioElement.buffered.length > 0) {
      patch.buffered = audioElement.buffered.end(audioElement.buffered.length - 1);
    }
    useAudioStore.getState()._setPlaybackState(patch);
  }, 100);
}

function stopPolling() {
  if (pollInterval) {
    clearInterval(pollInterval);
    pollInterval = null;
  }
}

function ensureAudioElement() {
  if (typeof window === "undefined") return null;
  if (!audioElement) {
    audioElement = new Audio();
    audioElement.preload = "metadata";
  }
  if (!listenersBound && audioElement) {
    audioElement.addEventListener("timeupdate", () => {
      if (!audioElement) return;
      const patch: Record<string, number> = { currentTime: audioElement.currentTime };
      if (audioElement.buffered.length > 0) {
        patch.buffered = audioElement.buffered.end(audioElement.buffered.length - 1);
      }
      useAudioStore.getState()._setPlaybackState(patch);
    });
    audioElement.addEventListener("loadedmetadata", () => {
      const duration = audioElement?.duration;
      if (duration && Number.isFinite(duration)) {
        useAudioStore.getState()._setPlaybackState({ duration });
      }
    });
    audioElement.addEventListener("durationchange", () => {
      const duration = audioElement?.duration;
      if (duration && Number.isFinite(duration)) {
        useAudioStore.getState()._setPlaybackState({ duration });
      }
    });
    audioElement.addEventListener("play", () => {
      useAudioStore.getState()._setPlaybackState({ isPlaying: true });
      startPolling();
    });
    audioElement.addEventListener("pause", () => {
      useAudioStore.getState()._setPlaybackState({ isPlaying: false });
      stopPolling();
    });
    audioElement.addEventListener("progress", () => {
      if (!audioElement || !audioElement.buffered.length) return;
      const end = audioElement.buffered.end(audioElement.buffered.length - 1);
      useAudioStore.getState()._setPlaybackState({ buffered: end });
    });
    audioElement.addEventListener("ended", () => {
      if (!audioElement) return;
      stopPolling();
      audioElement.currentTime = 0;
      useAudioStore.getState()._setPlaybackState({
        isPlaying: false,
        currentTime: 0,
      });
    });
    listenersBound = true;
  }
  return audioElement;
}

async function playCurrentAudio() {
  const audio = ensureAudioElement();
  if (!audio) return;
  await audio.play();
}

export const useAudioStore = create<AudioState>((set, get) => ({
  activeTrack: null,
  isPlaying: false,
  currentTime: 0,
  duration: null,
  buffered: 0,
  playbackRate: 1,

  loadTrack: (track, autoplay = false) => {
    const audio = ensureAudioElement();
    if (!audio) return;
    const isSameTrack = get().activeTrack?.id === track.id;
    set({
      activeTrack: track,
      currentTime: isSameTrack ? get().currentTime : 0,
      duration: track.duration ?? null,
      buffered: isSameTrack ? get().buffered : 0,
    });
    if (!isSameTrack || audio.src !== track.src) {
      audio.src = track.src;
      audio.currentTime = 0;
      set({ currentTime: 0 });
    }
    if (autoplay) {
      void playCurrentAudio();
    }
  },

  toggleTrack: (track) => {
    const current = get().activeTrack;
    if (current?.id === track.id) {
      get().togglePlayback();
      return;
    }
    get().loadTrack(track, true);
  },

  togglePlayback: () => {
    const audio = ensureAudioElement();
    if (!audio || !get().activeTrack) return;
    if (audio.paused) {
      void playCurrentAudio();
    } else {
      audio.pause();
    }
  },

  seek: (time) => {
    const audio = ensureAudioElement();
    if (!audio) return;
    audio.currentTime = Math.max(0, time);
    set({ currentTime: audio.currentTime });
  },

  cycleSpeed: () => {
    const speeds = [1, 1.5, 2, 0.5];
    const current = get().playbackRate;
    const idx = speeds.indexOf(current);
    const next = speeds[(idx + 1) % speeds.length];
    const audio = ensureAudioElement();
    if (audio) audio.playbackRate = next;
    set({ playbackRate: next });
  },

  stop: () => {
    stopPolling();
    const audio = ensureAudioElement();
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      audio.src = "";
    }
    set({
      activeTrack: null,
      isPlaying: false,
      currentTime: 0,
      duration: null,
      buffered: 0,
      playbackRate: 1,
    });
  },

  _setPlaybackState: (patch) => set(patch),
}));

export function __resetAudioStoreForTests() {
  if (audioElement) {
    audioElement.pause();
    audioElement.src = "";
    audioElement.currentTime = 0;
  }
  useAudioStore.setState({
    activeTrack: null,
    isPlaying: false,
    currentTime: 0,
    duration: null,
    buffered: 0,
    playbackRate: 1,
  });
}
